import { useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { useMutation } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  readonly jobId: Id<'jobs'>
  readonly reminderDays: readonly number[] | undefined
}

export const ReminderCadenceEditor = ({ jobId, reminderDays }: Props) => {
  const { t } = useTranslation()
  const [days, setDays] = useState<readonly number[]>(reminderDays ?? [])
  const [inputValue, setInputValue] = useState('')

  const { mutateAsync: updateJob, isPending } = useMutation({
    mutationFn: useConvexMutation(api.features.jobs.jobs.updateMyJob),
  })

  const handleAdd = (): void => {
    const day = parseInt(inputValue, 10)
    if (Number.isNaN(day) || day < 1) return
    if (days.includes(day)) return

    const updated = [...days, day].sort((a, b) => a - b)
    setDays(updated)
    setInputValue('')
    updateJob({ jobId, reminderDays: updated })
  }

  const handleRemove = (day: number): void => {
    const updated = days.filter((d) => d !== day)
    setDays(updated)
    updateJob({ jobId, reminderDays: updated })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') handleAdd()
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-primary/50">
        {t('jobs.settings.reminderCadenceDescription')}
      </p>

      {days.length === 0 ? (
        <p className="text-xs text-primary/40">
          {t('jobs.settings.reminderCadenceDefault')}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {days.map((day) => (
            <span
              key={day}
              className="flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-primary"
            >
              {t('jobs.settings.reminderDay', { day: String(day) })}
              <button
                type="button"
                onClick={() => handleRemove(day)}
                className="text-primary/40 hover:text-primary"
                aria-label={t('jobs.settings.removeReminderDay')}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={1}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('jobs.settings.reminderDayPlaceholder')}
          className="w-28"
          disabled={isPending}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleAdd}
          disabled={isPending || inputValue === ''}
        >
          <Plus className="h-3.5 w-3.5" />
          {t('jobs.settings.addReminderDay')}
        </Button>
      </div>
    </div>
  )
}
